import { useEffect, useMemo, useRef } from "react";

// Interfaces
import { IMiniDeal, IMiniDealTier } from "../interfaces";
import { IMiniDealModel } from "./interfaces";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";

// Hooks
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { useValidations } from "@/Common/hooks/Validations";

// Layouts
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";
import RoundBoxDeletable from "@/Layouts/components/RoundBoxDeletable";
import TierItemsLayout from "@/Layouts/components/Tiers/TierItemsLayout";

// Custom components
import CommissionCap from "../CommissionCap";
import ModelSelector from "../ModelSelector";

// MUI
import { Stack } from "@mui/material";

// Constants
const MAX_INPUT_VALUE = 1000000;

export default function MiniDealModel(props: Readonly<IMiniDealModel>) {
	const {
		index = 0,
		data = null,
		allowSpecificModels = false,
		deletable = false,
		modelSelectable = false,
		error = false,
		readOnly = false,
		onActionDelete = () => {},
		onActionUpdate = () => {},
		onActionValidation = () => {},
	} = props;

	// Hooks
	const { errors, isValid, getErrorsMessages, validate } = useValidations();
	const errorsMessages = getErrorsMessages();

	if (error) {
		errorsMessages.push("Invalid model and years definition");
	}

	// Control to send update actions
	const wasUpdated = useRef(false);

	const onMiniDealUpdate = (updatedMiniDeal: IMiniDeal) => {
		onActionValidation?.(isValid);
		if (isValid && wasUpdated.current) {
			onActionUpdate(updatedMiniDeal);
		}
	};

	// States
	const [miniDeal, setMiniDeal] = useCallbackState(null, onMiniDealUpdate);

	const handleDeleteClick = () => {
		onActionDelete(index);
	};

	const handleAddTier = () => {
		const actualTiers = [...miniDeal.tiers];
		const lastIndex = actualTiers?.length ? actualTiers.length - 1 : 1;

		actualTiers.push({
			from: { value: 0.01, input: "Dollar" },
			to: { value: MAX_INPUT_VALUE, input: "Dollar" },
			commission: { value: 0, input: "Dollar" },
			addCommission: { value: 0, input: "Percent" },
		});

		if (actualTiers?.length) {
			actualTiers[lastIndex].to.value = 0;
		}

		handleUpdateTiers(actualTiers);
	};

	const handleRemoveTier = () => {
		const actualTiers = [...miniDeal.tiers];
		if (actualTiers.length > 2) {
			actualTiers.splice(-1);
		}
		const lastIndex = actualTiers?.length ? actualTiers.length - 1 : 1;
		actualTiers[lastIndex].to.value = MAX_INPUT_VALUE;
		handleUpdateTiers(actualTiers);
	};

	const handleUpdateInput = (row: number, area: string, value: string) => {
		const actualTiers = [...miniDeal.tiers];
		if (actualTiers?.[row]?.[area]) {
			actualTiers[row][area]["value"] = value ? parseFloat(value) : 0;
		}
		if (actualTiers?.[row + 1] && area === "to") {
			actualTiers[row + 1]["from"]["value"] = (value ? Number(value) : 0) + 0.01;
		}
		handleUpdateTiers(actualTiers);
	};

	const handleUpdateMiniDeal = (content: Partial<IMiniDeal>) => {
		const updatedMiniDeal = { ...miniDeal, ...content };
		updateMiniDeal(updatedMiniDeal);
	};

	const handleUpdateTiers = (tiers: IMiniDealTier[]) => {
		const updatedMiniDeal = { ...miniDeal, tiers };
		updateMiniDeal(updatedMiniDeal);
	};

	const handleGross = (value: boolean) => {
		const updatedMiniDeal = { ...miniDeal, applyGrossRetroactively: value };
		updateMiniDeal(updatedMiniDeal);
	};

	const handleCap = (area: string, value: number | string | boolean) => {
		const updatedMiniDeal = { ...miniDeal, [area]: value };
		if (area === "capEnabled" && !value) {
			updatedMiniDeal.cap = 0;
		}
		updateMiniDeal(updatedMiniDeal);
	};

	const updateMiniDeal = (currentMiniDeal: IMiniDeal) => {
		// Update
		setMiniDeal(currentMiniDeal);
		wasUpdated.current = true;
	};

	useMemo(() => {
		// Execute validations
		if (miniDeal?.tiers?.length) {
			validate([
				{
					definition: {
						useKey: "totalGrossProfit",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: "<", // If from.value < to.value is good,
									constraints: { notCrossZero: true },
								},
								{
									type: "STEP",
									on: "value",
									compare: "===",
									deltas: { Dollar: 0.01 }, // Add delta on upper value
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Invalid tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: miniDeal.tiers,
				},
			]);
		}
	}, [miniDeal, validate]);

	const disabledRowsAttributes = useMemo(() => {
		if (!miniDeal?.tiers?.length) {
			return [];
		}
		return miniDeal?.tiers?.map((tier: IMiniDealTier) => {
			return { addCommission: Boolean(Number(tier?.to?.value) <= Number(0)) };
		});
	}, [miniDeal?.tiers]);

	useEffect(() => {
		setMiniDeal(clone(data));
		return () => {
			setMiniDeal(null);
			wasUpdated.current = false;
		};
	}, [data, setMiniDeal]);

	return (
		<RoundBoxDeletable
			title={miniDeal?.make}
			deletable={deletable}
			deleteDisabled={readOnly}
			confirmationDeleteSx={{
				right: "-2px !important",
			}}
			onDeleteAction={handleDeleteClick}
		>
			{Boolean(modelSelectable) && (
				<Stack>
					<ModelSelector
						make={miniDeal?.make}
						model={miniDeal?.model}
						modelYears={miniDeal?.modelYears}
						allowSpecificModels={allowSpecificModels}
						useRegexModels={true}
						error={error}
						readOnly={readOnly || !isValid}
						onActionUpdate={(model) => {
							handleUpdateMiniDeal(model);
						}}
					/>
				</Stack>
			)}

			<Stack py={1}>
				<CustomizedSwitches
					checked={miniDeal?.applyGrossRetroactively}
					textChecked="Apply on gross retroactively"
					textUnChecked="Apply on gross retroactively"
					disabled={readOnly}
					onChange={handleGross}
				/>
			</Stack>

			<Stack mb={2} sx={{ minHeight: "100px" }}>
				<TierItemsLayout
					minimumRows={2}
					rows={miniDeal?.tiers || []}
					columns={[
						{
							_id: "totalGrossProfit",
							title: "Total Gross Profit",
							component: {
								type: "DoubleInput",
								order: ["from", "to"],
								disableCommonInputs: true,
								properties: {
									firstInput: {
										data: [
											{
												type: "Dollar",
												min: -MAX_INPUT_VALUE,
												max: MAX_INPUT_VALUE,
												decimalsEditable: true,
												decimals: 2,
												disabled: true,
											},
										],
										sx: { maxWidth: "95px" },
									},
									secondInput: {
										data: [
											{
												type: "Dollar",
												min: -MAX_INPUT_VALUE,
												max: MAX_INPUT_VALUE,
												decimalsEditable: true,
												decimals: 2,
											},
										],
										sx: { maxWidth: "95px" },
									},
								},
								callbacks: {
									onActionUpdate: (positions: { row: number }, area: string, value: string) => {
										handleUpdateInput(positions.row, area, value);
									},
								},
							},
						},
						{
							_id: "commission",
							title: "Commission",
							component: {
								type: "DoubleInput",
								order: ["commission", "addCommission"],
								properties: {
									separatorText: "+",
									firstInput: {
										data: [
											{
												type: "Dollar",
												min: 0,
												max: MAX_INPUT_VALUE,
												decimalsEditable: true,
												decimals: 2,
											},
										],
										sx: { maxWidth: "95px" },
									},
									secondInput: {
										data: [
											{
												type: "Percent",
												min: 0,
												max: 100,
												decimalsEditable: true,
												decimals: 2,
											},
										],
										sx: { maxWidth: "75px" },
									},
								},
								callbacks: {
									onActionUpdate: (positions: { row: number }, area: string, value: string) => {
										handleUpdateInput(positions.row, area, value);
									},
								},
							},
						},
					]}
					disabledRowsAttributes={disabledRowsAttributes}
					errors={errors}
					errorsMessages={errorsMessages}
					readOnly={readOnly}
					onActionAdd={handleAddTier}
					onActionDelete={handleRemoveTier}
				/>
			</Stack>

			<Stack mb={2}>
				<CommissionCap
					value={miniDeal?.cap}
					enabled={Boolean(miniDeal?.capEnabled)}
					readOnly={readOnly}
					onUpdateCapValue={(value) => handleCap("cap", value)}
					onUpdateCapSwitch={(value) => handleCap("capEnabled", value)}
				/>
			</Stack>
		</RoundBoxDeletable>
	);
}
